import { z } from 'zod';

import { ApiError } from '../../../api';
import type { ApiViolation } from '../../../api';
import {
  ARCHETYPES,
  CADENCE_UNITS,
  DIRECTIONS,
  DUE_BASES,
  MAINTENANCE_LIMITS,
  PERFORMED_BY,
  PROGRAMMES,
  READING_KINDS,
  REASON_LIMITS,
  SEVERITIES,
  TIERS,
} from '../constants/vocabulary';

export const INVALID_REQUEST = 'INVALID_REQUEST';

const STATUS_BAD_REQUEST = 400;
const FIRST_LIBRARY_VERSION = 1;
const FIRST_ENTRY_VERSION = 0;
const FIRST_CADENCE = 1;

const text = z.string();
const filled = z.string().refine((value) => value.trim() !== '');
const reason = z.string().trim().min(REASON_LIMITS.change.min).max(REASON_LIMITS.change.max);
const finite = z.number().finite();
const libraryVersion = z.number().int().min(FIRST_LIBRARY_VERSION);
const entryVersion = z.number().int().min(FIRST_ENTRY_VERSION);
const severity = z.enum(SEVERITIES);
const direction = z.enum(DIRECTIONS);
const listOf = <T extends z.ZodTypeAny>(item: T) => z.array(item).readonly();

const bandInput = z.object({ severity, threshold: finite }).strict();

const cadenceInput = z.object({ n: z.number().int().min(FIRST_CADENCE), unit: z.enum(CADENCE_UNITS) }).strict();

const storedRow = z.object({}).passthrough();

const savedBand = z.object({ severity, threshold: finite }).passthrough();

const savedCadence = z.object({ n: finite.min(FIRST_CADENCE), unit: z.enum(CADENCE_UNITS) }).passthrough();

const savedReading = z
  .object({
    name: filled,
    kind: z.enum(READING_KINDS),
    valid: z.object({ min: finite, max: finite }).passthrough(),
  })
  .passthrough();

const savedAlert = z
  .object({
    key: filled,
    reading: filled,
    direction,
    bands: listOf(savedBand),
  })
  .passthrough();

const savedSchedule = z
  .object({
    key: filled,
    every: savedCadence.nullable(),
    meterHours: finite.nullable(),
  })
  .passthrough();

const savedConditionJob = z
  .object({
    key: filled,
    reading: filled,
    doWhen: finite,
    doneWhen: finite,
  })
  .passthrough();

const savedNextPhase = z
  .object({
    readings: listOf(storedRow),
    alerts: listOf(storedRow),
    work: listOf(storedRow),
  })
  .passthrough();

export const createLibraryTypeBodySchema = z
  .object({
    code: filled,
    name: filled,
    archetype: z.enum(ARCHETYPES),
    tier: z.enum(TIERS),
    tagPrefix: filled,
    reason,
  })
  .strict();

export const updateLibraryTypeBodySchema = z
  .object({
    name: filled,
    archetype: z.enum(ARCHETYPES),
    holdsWater: z.boolean(),
    tier: z.enum(TIERS),
    expects: listOf(z.enum(PROGRAMMES)),
    tagPrefix: filled,
    readings: listOf(savedReading),
    tagMap: z.record(text, text),
    alerts: listOf(savedAlert),
    schedules: listOf(savedSchedule),
    conditionJobs: listOf(savedConditionJob),
    formRules: listOf(storedRow),
    nextPhase: savedNextPhase,
    cycles: z.boolean(),
    confirm: text.nullable(),
    expectedVersion: libraryVersion,
    reason,
  })
  .passthrough();

export const archiveLibraryTypeBodySchema = z
  .object({
    expectedVersion: libraryVersion,
    reason,
  })
  .strict();

export const versionedBodySchema = z
  .object({
    expectedVersion: entryVersion,
  })
  .strict();

export const setTierBodySchema = z
  .object({
    tier: z.enum(TIERS).nullable(),
    reason,
    expectedVersion: entryVersion,
  })
  .strict();

export const putOverrideBodySchema = z
  .object({
    off: z.boolean().optional(),
    direction: direction.optional(),
    bands: listOf(bandInput).optional(),
    basis: z.enum(DUE_BASES).optional(),
    every: cadenceInput.optional(),
    meterHours: finite.positive().optional(),
    performedBy: z.enum(PERFORMED_BY).optional(),
    doWhen: finite.optional(),
    doneWhen: finite.optional(),
    reason,
    expectedVersion: entryVersion,
  })
  .strict();

export const setMaintenanceBodySchema = z
  .object({
    reason,
    plannedHours: z.number().gt(MAINTENANCE_LIMITS.minHours).max(MAINTENANCE_LIMITS.maxHours),
    note: z.string().max(MAINTENANCE_LIMITS.noteMax).optional(),
    suppressedAlertKeys: listOf(text).optional(),
    expectedVersion: entryVersion,
  })
  .strict();

const violationOf = (issue: z.ZodIssue): ApiViolation => ({
  code: issue.code,
  message: issue.message,
  path: issue.path.join('.'),
  keys: issue.code === z.ZodIssueCode.unrecognized_keys ? issue.keys : undefined,
});

const placesOf = (issues: readonly z.ZodIssue[]): string =>
  issues.map((issue) => (issue.code === z.ZodIssueCode.unrecognized_keys ? issue.keys.join(', ') : issue.path.join('.'))).join('; ');

export const invalidRequestOf = (label: string, issues: readonly z.ZodIssue[]): ApiError =>
  new ApiError({
    status: STATUS_BAD_REQUEST,
    code: INVALID_REQUEST,
    message: `The ${label} was not sent: the server would refuse ${placesOf(issues)}`,
    violations: issues.map(violationOf),
  });

export const isInvalidRequest = (error: unknown): boolean => error instanceof ApiError && error.code === INVALID_REQUEST;
